import { isNonNullableObject } from "@knyt/artisan";
import { isLifecycleDelegateHost, LifecycleAdapter } from "./LifecycleDelegate.js";
/**
 * This symbol is used to attach the lifecycle to an object
 * that implements the `Lifecycle` interface.
 *
 * @remarks
 *
 * This symbol must be in the runtime-wide symbol registry
 * (`Symbol.for`) so that the reactive adapter can be
 * accessed from within different contexts.
 *
 * @internal scope: workspace
 */
export const __lifecycle = Symbol.for("knyt.tasker.lifecycle");
/**
 * Determines whether the input is a {@link Lifecycle}.
 */
export function isLifecycle(value) {
    return (isNonNullableObject(value) &&
        __lifecycle in value &&
        isLifecycleDelegateHost(value[__lifecycle]));
}
/**
 * Assert that the object is a {@link Lifecycle}.
 */
export function assertLifecycle(value) {
    if (!isLifecycle(value)) {
        throw new TypeError("Object is not controllable");
    }
}
const mixedConstructors = new WeakSet();
/**
 * Applies the lifecycle mixin to a constructor.
 */
export function applyLifecycleMixin(Constructor, members = []) {
    if (mixedConstructors.has(Constructor))
        return;
    mixedConstructors.add(Constructor);
    const proto = Constructor.prototype;
    for (const memberName of members) {
        if (typeof LifecycleAdapter.prototype[memberName] !== "function") {
            throw new Error(`Method "${memberName}" does not exist on the LifecycleAdapter prototype.`);
        }
        if (memberName in proto) {
            throw new Error(`Member "${memberName}" already exists on the prototype. Please rename the member.`);
        }
        proto[memberName] = function (...args) {
            // TODO: Remove in production.
            assertLifecycle(this);
            return this[__lifecycle][memberName](...args);
        };
    }
}
